import _ from 'underscore';
import { mapColumnsConfig } from '@bingads-webui/projection-grid-base-column';
import { RECOMMENDATION_TYPES, ESTIMATE_TYPES } from '@bingads-webui-campaign/recommendation-core';
import { RECOMMENDATION_INLINE_ACTION_TYPE } from '@bingads-webui-campaign/recommendation-inline-action';
import { INLINE_ACTION_MAP, NO_ACTION_RECOMMENDATION_TYPES } from '../const';

import { ActionApplyOnlyColumn } from './action-apply-only-column';
import { ActionApplyEditColumn } from './action-edit-apply-column';
import { ActionResubmitEditColumn } from './action-edit-resubmit-column';
import { ActionEditOnlyColumn } from './action-edit-only-column';
import { ActionExternalColumn } from './action-external-column';
import { ActionPanelEditApply } from './action-panel-edit-apply-column';
import { ActionRepairColumn } from './action-repair-column';
import { ActionSetupConversionTrackingColumn } from './action-setup-conversion-tracking-column';
import { CampaignColumn } from './campaign-column';
import { AccountColumn } from './account-column';
import { CampaignAdGroupColumn } from './campaign-ad-group-column';
import { AdGroupColumn } from './ad-group-column';
import { ClicksDeltaColumn } from './click-delta-column';
import { CostDeltaColumn } from './cost-delta-column';
import { ImpressionsDeltaColumn } from './impression-delta-column';
import { KeywordColumn } from './keyword-column';
import { MatchTypeColumn } from './match-type-column';
import { GoogleImportToDoColumn } from './google-import-todo-column';
import { BudgetDeltaColumn } from './budget-delta-column';
import { SharedBudgetDeltaColumn } from './shared-budget-delta-column';
import { ConversionDeltaColumn } from './conversion-delta-column';
import { BidAdjustmentTodoColumn } from './bid-adjustment-todo-column';
import { CtrDeltaColumn } from './ctr-delta-column';
import { CurrentMatchTypeColumn } from './current-match-type-column';
import { SuggestedBidColumn } from './suggested-bid-column';
import { SuggestedKeywordColumn } from './suggested-keyword-column';
import { TopSearchTermColumn } from './top-search-term-column';
import { RecommendedBecauseColumn } from './recommended-because-column';
import { MatchTypeSelectorColumn } from './match-type-selector-column';
import { MatchTypeSelectorColumnVNext } from './match-type-selector-column-vnext';
import { PreviewRSAColumn } from './preview-rsa-column';
import { RemoveNegativeKeywordColumn } from './remove-negative-keyword-column';
import { BlockedKeywordsColumn } from './blocked-keywords-column';
import { SuggestedCampaignColumn } from './suggested-campaign-column';
import { EstimateSearchColumn } from './estimate-search-column';
import { SearchExampleColumn } from './search-example-column';
import { CurrentClickColumn } from './current-click-column';
import { CurrentCostColumn } from './current-cost-column';
import { CurrentImpressionColumn } from './current-impression-column';
import { LandingPageUrlColumn } from './landing-page-url-column';
import { RejectionTypeColumn } from './rejection-type-column';
import { NumberRejectedAdsColumn } from './number-rejected-ads-column';
import { NumberRejectedKeywordsColumn } from './number-rejected-keywords-column';
import { RejectedAdsColumn } from './rejected-ads-column';
import { RejectedKeywordsColumn } from './rejected-keywords-column';
import { PolicyColumn } from './policy-column';
import { SetupConversionTrackingToDoColumn } from './setup-conversion-tracking-todo-column';
import { NumberClicksColumn } from './number-clicks-column';
import { NumberImpressionsColumn } from './number-impressions-column';
import { PerformanceSummaryColumn } from './performance-summary-column';
import { FixConversionTrackingTodoColumn } from './fix-conversion-tracking-todo-column';
import { FixConversionTrackingSummaryColumn } from './fix-conversion-tracking-summary-column';
import { AssetGeneratedFromColumn } from './asset-generated-from';
import { GoalColumn } from './goal-column';
import { GoalTypeColumn } from './goal-type-column';
import { CurrentSettingColumn } from './current-setting-column';
import { SuggestedSettingColumn } from './suggested-setting-column';
import { MissedConversionColumn } from './missed-conversion-column';
import { SearchVolumeDeltaColumn } from './search-volume-delta-column';
import { SearchVolumeColumn } from './search-volume-column';
import { GoalSettingColumn } from './goal-setting-column';
import { GeneratedFromColumn } from './generated-from-column';
import { IssueTypeColumn } from './issue-type-column';
import { ActionHybridColumn } from './action-hybrid-column';
import { FacebookImportToDoColumn } from './facebook-import-todo-column';
import { PreviewMMAColumn } from './preview-mma-column';
import { PreviewImageExtensionColumn } from './preview-image-extension-column';
import { ActionImageExtensionColumn } from './action-image-extension-column';
import { ImageExtensionSourceColumn } from './image-extension-source-column';
import { MatchTypeNewColumn } from './match-type-new-column';
import { ParamColumn } from './param-column';
import { SuggestedParamValueColumn } from './suggested-param-value-column';
import { PreviewImproveRSAColumn } from './preview-improve-rsa-column';
import { ProductGroupColumn } from './product-group-column';
import { CampaignBudgetColumn } from './campaign-budget-column';
import { CampaignPriorityColumn } from './campaign-priority-column';
import { StoreCountryColumn } from './store-country-column';
import { ProductTargetedColumn } from './product-targeted-column';
import { SuggestedBidProductGroupColumn } from './suggested-bid-product-group-column';
import { SuggestedShoppingCampaignColumn } from './suggested-shopping-campaign-column';
import { SuggestedShoppingAdGroupColumn } from './suggested-shopping-adgroup-column';
import { PreviewImproveMMAColumn } from './preview-improve-mma-column';
import { DSALevelTypeColumn } from './dsa-level-type-column';
import { StoreColumn } from './store-column';
import { CountryOrRegionColumn } from './country-or-region-column';
import { PerformanceMaxImportDescriptionColumn } from './performance-max-import-description-column';
import { PerformanceMaxImportToDoColumn } from './performance-max-import-todo-column';
import { UpgradedCampaignColumn } from './upgraded-campaign-column';
import { CPCAvgDeltaColumn } from './cpc-avg-delta-column';
import { CPADeltaColumn } from './cpa-delta-column';

const actionTypeTemplateMapping = {
  [RECOMMENDATION_INLINE_ACTION_TYPE.APPLY_ONLY]: ActionApplyOnlyColumn,
  [RECOMMENDATION_INLINE_ACTION_TYPE.APPLY_EDIT]: ActionApplyEditColumn,
  [RECOMMENDATION_INLINE_ACTION_TYPE.APPLY_PANEL_EDIT]: ActionPanelEditApply,
  [RECOMMENDATION_INLINE_ACTION_TYPE.EDIT_ONLY]: ActionEditOnlyColumn,
  [RECOMMENDATION_INLINE_ACTION_TYPE.EXTERNAL]: ActionExternalColumn,
  [RECOMMENDATION_INLINE_ACTION_TYPE.REPAIR]: ActionRepairColumn,
  [RECOMMENDATION_INLINE_ACTION_TYPE.RESUBMIT_EDIT]: ActionResubmitEditColumn,
  [RECOMMENDATION_INLINE_ACTION_TYPE.SETUP_CONV]: ActionSetupConversionTrackingColumn,
  [RECOMMENDATION_INLINE_ACTION_TYPE.HYBRID]: ActionHybridColumn,
  [RECOMMENDATION_INLINE_ACTION_TYPE.IMAGE_EXTENSION]: ActionImageExtensionColumn,
};

const estimateTypeDefaultSortColumnMapping = {
  [ESTIMATE_TYPES.CONVERSIONS]: ConversionDeltaColumn,
  [ESTIMATE_TYPES.CLICKS]: ClicksDeltaColumn,
  [ESTIMATE_TYPES.COST]: CostDeltaColumn,
  [ESTIMATE_TYPES.CTR]: CtrDeltaColumn,
  [ESTIMATE_TYPES.IMPRESSIONS]: ImpressionsDeltaColumn,
};

function getSortableHeaderConfigNull() {
  return {};
}

function getSortableHeaderConfigByColumn(Column, i18n, directionIndex = 1) {
  if (_.isUndefined(Column)) {
    return getSortableHeaderConfigNull();
  }
  const { name } = new Column({ i18n });
  return {
    name,
    directionIndex,
  };
}

function getSortableHeaderConfigByPrimaryEstimate(i18n, primaryEstimate) {
  const Column = estimateTypeDefaultSortColumnMapping[primaryEstimate];
  const directionIndex = (primaryEstimate === ESTIMATE_TYPES.COST) ? 0 : 1;
  return getSortableHeaderConfigByColumn(Column, i18n, directionIndex);
}

const adExtensionColumns = [
  CampaignColumn,
  ClicksDeltaColumn,
  CtrDeltaColumn,
  CostDeltaColumn,
];

const adExtensionColumnsMCC = [
  CampaignColumn,
  AccountColumn,
  ClicksDeltaColumn,
  CtrDeltaColumn,
  CostDeltaColumn,
];

const SetupConversionTrackingOpportunityColumnsMCC = [
  SetupConversionTrackingToDoColumn,
  NumberImpressionsColumn,
  NumberClicksColumn,
];

const budgetColumns = [
  BudgetDeltaColumn, // What you can do
  ClicksDeltaColumn, // Weekly Est. Clicks
  ConversionDeltaColumn, // Weekly Est. Conversion
  CostDeltaColumn, // Weekly Est. Spend
];

const adjustSharedBudgetColumns = [
  SharedBudgetDeltaColumn, // What you can do
  ClicksDeltaColumn, // Weekly Est. Clicks
  ConversionDeltaColumn, // Weekly Est. Conversion
  CostDeltaColumn, // Weekly Est. Spend
];

const broadMatchKeywordColumns = [
  KeywordColumn,
  SuggestedBidColumn,
  MatchTypeColumn,
  CurrentMatchTypeColumn,
  AdGroupColumn,
  CampaignColumn,
  TopSearchTermColumn,
  RecommendedBecauseColumn,
];

const broadMatchKeywordColumnsVNext = [
  KeywordColumn,
  SuggestedBidColumn,
  MatchTypeColumn,
  CurrentMatchTypeColumn,
  CampaignAdGroupColumn,
  TopSearchTermColumn,
  RecommendedBecauseColumn,
];

const broadMatchKeywordColumnsVNextMCC = [
  KeywordColumn,
  SuggestedBidColumn,
  MatchTypeColumn,
  CurrentMatchTypeColumn,
  CampaignAdGroupColumn,
  AccountColumn,
  TopSearchTermColumn,
  RecommendedBecauseColumn,
];

const googleImportColumns = [
  GoogleImportToDoColumn,
  ImpressionsDeltaColumn,
  ClicksDeltaColumn,
];

const newKeywordColumns = [
  SuggestedKeywordColumn,
  SuggestedBidColumn,
  MatchTypeSelectorColumn,
  AdGroupColumn,
  CampaignColumn,
  TopSearchTermColumn,
  RecommendedBecauseColumn,
];

const newKeywordColumnsVNext = [
  SuggestedKeywordColumn,
  SuggestedBidColumn,
  MatchTypeSelectorColumnVNext,
  CampaignAdGroupColumn,
  TopSearchTermColumn,
  RecommendedBecauseColumn,
];

const newKeywordColumnsVNextMCC = [
  SuggestedKeywordColumn,
  SuggestedBidColumn,
  MatchTypeSelectorColumnVNext,
  CampaignAdGroupColumn,
  AccountColumn,
  TopSearchTermColumn,
  RecommendedBecauseColumn,
];

const trendingQueryColumns = [
  SuggestedKeywordColumn,
  SuggestedBidColumn,
  MatchTypeSelectorColumnVNext,
  CampaignAdGroupColumn,
  SearchVolumeDeltaColumn,
  SearchVolumeColumn,
];

const trendingQueryColumnsMCC = [
  SuggestedKeywordColumn,
  SuggestedBidColumn,
  MatchTypeSelectorColumnVNext,
  CampaignAdGroupColumn,
  AccountColumn,
  SearchVolumeDeltaColumn,
  SearchVolumeColumn,
];

const repairAdsColumns = [
  AdGroupColumn,
  CampaignColumn,
  // SuggestedAdsColumn, [TODO]: uncomment if suggested ads come back
];

const repairAdsColumnsMCC = [
  AdGroupColumn,
  CampaignColumn,
  AccountColumn,
];

// [TODO]: Add Repair Keywords columns
const repairKeywordsColumns = [
  AdGroupColumn,
  CampaignColumn,
];

const repairKeywordsColumnsMCC = [
  AdGroupColumn,
  CampaignColumn,
  AccountColumn,
];

const fixAdTextColumns = [
  PolicyColumn,
  RejectedAdsColumn,
  RejectedKeywordsColumn,
];

const fixAdTextColumnsMCC = [
  PolicyColumn,
  AccountColumn,
  RejectedAdsColumn,
  RejectedKeywordsColumn,
];

const removeConflictingNKWColumns = [
  RemoveNegativeKeywordColumn,
  BlockedKeywordsColumn,
];

const dynamicSearchAdsColumns = [
  SuggestedCampaignColumn,
  DSALevelTypeColumn,
  EstimateSearchColumn,
  SearchExampleColumn,
];

const dynamicSearchAdsColumnsMCC = [
  SuggestedCampaignColumn,
  AccountColumn,
  DSALevelTypeColumn,
  EstimateSearchColumn,
  SearchExampleColumn,
];

const getResponsiveSearchAdsColumns = (permissions, isMCC) => {
  const columns = [];
  columns.push(PreviewRSAColumn);
  columns.push(AdGroupColumn);
  columns.push(CampaignColumn);
  if (permissions.IsRecommendationTypeResponsiveSearchAdsNewTextsEnabled) {
    columns.push(AssetGeneratedFromColumn);
  }
  if (isMCC) {
    columns.push(AccountColumn);
  }
  columns.push(CurrentClickColumn);
  columns.push(CurrentImpressionColumn);
  columns.push(CurrentCostColumn);
  return columns;
};

const ReallocateBudgetColumns = [
  BidAdjustmentTodoColumn,
  ClicksDeltaColumn,
  CostDeltaColumn,
];

const FixAdsDestinationColumns = [
  LandingPageUrlColumn,
  RejectionTypeColumn,
  NumberRejectedAdsColumn,
  NumberRejectedKeywordsColumn,
];

const fixAdsDestinationColumnsMCC = [
  LandingPageUrlColumn,
  RejectionTypeColumn,
  AccountColumn,
  NumberRejectedAdsColumn,
  NumberRejectedKeywordsColumn,
];

const SetupConversionTrackingOpportunityColumns = [
  SetupConversionTrackingToDoColumn,
  PerformanceSummaryColumn,
];

const FixConversionTrackingColumns = [
  FixConversionTrackingTodoColumn,
  FixConversionTrackingSummaryColumn,
];

const fixConversionGoalSettingsColumns = [
  GoalColumn,
  IssueTypeColumn,
  CurrentSettingColumn,
  SuggestedSettingColumn,
  MissedConversionColumn,
];

const createConversionGoalColumns = [
  GoalColumn,
  GoalTypeColumn,
  GeneratedFromColumn,
  GoalSettingColumn,
  MissedConversionColumn,
];

const facebookImportColumns = [
  FacebookImportToDoColumn,
  ImpressionsDeltaColumn,
  ClicksDeltaColumn,
  CostDeltaColumn,
];

const multiMediaAdsColumns = [
  PreviewMMAColumn,
  AdGroupColumn,
  CampaignColumn,
  AssetGeneratedFromColumn,
  CurrentClickColumn,
  CurrentImpressionColumn,
  CurrentCostColumn,
];

const multiMediaAdsColumnsMCC = [
  PreviewMMAColumn,
  AdGroupColumn,
  CampaignColumn,
  AssetGeneratedFromColumn,
  AccountColumn,
  CurrentClickColumn,
  CurrentImpressionColumn,
  CurrentCostColumn,
];

const improveMultiMediaAdsColumns = [
  PreviewImproveMMAColumn,
  AdGroupColumn,
  CampaignColumn,
  ImpressionsDeltaColumn,
  ClicksDeltaColumn,
  CostDeltaColumn,
];

const improveMultiMediaAdsColumnsMCC = [
  PreviewImproveMMAColumn,
  AdGroupColumn,
  CampaignColumn,
  AccountColumn,
  ImpressionsDeltaColumn,
  ClicksDeltaColumn,
  CostDeltaColumn,
];

const imageExtensionColumns = [
  PreviewImageExtensionColumn,
  CampaignColumn,
  AdGroupColumn,
  ImageExtensionSourceColumn,
  ClicksDeltaColumn,
];

const imageExtensionColumnsMCC = [
  PreviewImageExtensionColumn,
  CampaignColumn,
  AdGroupColumn,
  AccountColumn,
  ImageExtensionSourceColumn,
  ClicksDeltaColumn,
];

const repairMissingKeywordParamsColumns = [
  KeywordColumn,
  MatchTypeNewColumn,
  ParamColumn,
  SuggestedParamValueColumn,
  CampaignColumn,
  AdGroupColumn,
  ImpressionsDeltaColumn,
];

const repairMissingKeywordParamsColumnsMCC = [
  KeywordColumn,
  MatchTypeNewColumn,
  ParamColumn,
  SuggestedParamValueColumn,
  CampaignColumn,
  AdGroupColumn,
  AccountColumn,
  ImpressionsDeltaColumn,
];

const fixNoImpressionBidColumns = [
  KeywordColumn,
  SuggestedBidColumn,
  MatchTypeNewColumn,
  CampaignColumn,
  AdGroupColumn,
  ImpressionsDeltaColumn,
];

const fixNoImpressionBidColumnsMCC = [
  KeywordColumn,
  SuggestedBidColumn,
  MatchTypeNewColumn,
  CampaignColumn,
  AdGroupColumn,
  AccountColumn,
  ImpressionsDeltaColumn,
];

const improveResponsiveSearchAdsColumns = [
  PreviewImproveRSAColumn,
  AdGroupColumn,
  CampaignColumn,
  ImpressionsDeltaColumn,
  ClicksDeltaColumn,
  CostDeltaColumn,
];

const improveResponsiveSearchAdsColumnsMCC = [
  PreviewImproveRSAColumn,
  AdGroupColumn,
  CampaignColumn,
  AccountColumn,
  ImpressionsDeltaColumn,
  ClicksDeltaColumn,
  CostDeltaColumn,
];

const repairUntargetedOfferColumns = [
  SuggestedShoppingCampaignColumn,
  SuggestedShoppingAdGroupColumn,
  ProductGroupColumn,
  SuggestedBidProductGroupColumn,
  CampaignBudgetColumn,
  CampaignPriorityColumn,
  StoreCountryColumn,
  ProductTargetedColumn,
  ImpressionsDeltaColumn,
];

const repairUntargetedOfferColumnsMCC = [
  SuggestedShoppingCampaignColumn,
  SuggestedShoppingAdGroupColumn,
  ProductGroupColumn,
  AccountColumn,
  SuggestedBidProductGroupColumn,
  CampaignBudgetColumn,
  CampaignPriorityColumn,
  StoreCountryColumn,
  ProductTargetedColumn,
  ImpressionsDeltaColumn,
];

const SSC2PMaxMigrationColumns = [
  CampaignColumn,
  StoreColumn,
  CountryOrRegionColumn,
  CurrentCostColumn,
];

const SSC2PMaxMigrationColumnsMCC = [
  AccountColumn,
  CampaignColumn,
  StoreColumn,
  CountryOrRegionColumn,
  CurrentCostColumn,
];

const DSA2PMaxMigrationColumns = [
  CampaignColumn,
  UpgradedCampaignColumn,
  ConversionDeltaColumn,
  CostDeltaColumn,
];

const DSA2PMaxMigrationColumnsMCC = [
  AccountColumn,
  CampaignColumn,
  UpgradedCampaignColumn,
  ConversionDeltaColumn,
  CostDeltaColumn,
];

const PerformanceMaxImportColumns = [
  PerformanceMaxImportDescriptionColumn,
  PerformanceMaxImportToDoColumn,
];

const PerformanceMaxImportColumnsMCC = [
  AccountColumn,
  PerformanceMaxImportDescriptionColumn,
  PerformanceMaxImportToDoColumn,
];

const SyndicationGapColumns = [
  CampaignColumn,
  AdGroupColumn,
  ClicksDeltaColumn,
  ConversionDeltaColumn,
  CPCAvgDeltaColumn,
  CPADeltaColumn,
];

const SyndicationGapColumnsMCC = [
  AccountColumn,
  CampaignColumn,
  AdGroupColumn,
  ClicksDeltaColumn,
  ConversionDeltaColumn,
  CPCAvgDeltaColumn,
  CPADeltaColumn,
];

const getColumnsMap = (permissions) => {
  const isMCC = false;
  return {
    [RECOMMENDATION_TYPES.CALLOUT]: adExtensionColumns,
    [RECOMMENDATION_TYPES.SITE_LINK]: adExtensionColumns,
    [RECOMMENDATION_TYPES.REPAIR_ADS]: repairAdsColumns,
    [RECOMMENDATION_TYPES.REPAIR_KEYWORD]: repairKeywordsColumns,
    [RECOMMENDATION_TYPES.FIX_AD_TEXT]: fixAdTextColumns,
    [RECOMMENDATION_TYPES.BUDGET_OPPORTUNITY]: budgetColumns,
    [RECOMMENDATION_TYPES.ADJUST_SHARED_BUDGET_OPPORTUNITY]: adjustSharedBudgetColumns,
    [RECOMMENDATION_TYPES.GOOGLE_IMPORT]: googleImportColumns,
    [RECOMMENDATION_TYPES.GOOGLE_IMPORT_SCHEDULED]: googleImportColumns,
    [RECOMMENDATION_TYPES.BROAD_MATCH_KEYWORD]: broadMatchKeywordColumns,
    [`${RECOMMENDATION_TYPES.BROAD_MATCH_KEYWORD}VNext`]: broadMatchKeywordColumnsVNext,
    [RECOMMENDATION_TYPES.NEW_KEYWORD_OPPORTUNITY]: newKeywordColumns,
    [`${RECOMMENDATION_TYPES.NEW_KEYWORD_OPPORTUNITY}VNext`]: newKeywordColumnsVNext,
    [RECOMMENDATION_TYPES.TRENDING_QUERY]: trendingQueryColumns,
    [RECOMMENDATION_TYPES.REMOVE_CONFLICTING_NEGATIVE_KEYWORD]: removeConflictingNKWColumns,
    [RECOMMENDATION_TYPES.DYNAMIC_SEARCH_ADS]: dynamicSearchAdsColumns,
    [RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS]:
      getResponsiveSearchAdsColumns(permissions, isMCC),
    [RECOMMENDATION_TYPES.REALLOCATE_BUDGET]: ReallocateBudgetColumns,
    [RECOMMENDATION_TYPES.FIX_AD_DESTINATION]: FixAdsDestinationColumns,
    [RECOMMENDATION_TYPES.SETUP_CONVERSION_TRACKING_OPPORTUNITY]:
      SetupConversionTrackingOpportunityColumns,
    [RECOMMENDATION_TYPES.FIX_CONVERSION_TRACKING_OPPORTUNITY]: FixConversionTrackingColumns,
    [RECOMMENDATION_TYPES.FIX_CONVERSION_GOAL_SETTINGS]: fixConversionGoalSettingsColumns,
    [RECOMMENDATION_TYPES.CREATE_CONVERSION_GOAL]: createConversionGoalColumns,
    [RECOMMENDATION_TYPES.FACEBOOK_IMPORT]: facebookImportColumns,
    [RECOMMENDATION_TYPES.MULTIMEDIA_ADS]: multiMediaAdsColumns,
    [RECOMMENDATION_TYPES.IMAGE_EXTENSION]: imageExtensionColumns,
    [RECOMMENDATION_TYPES.REPAIR_MISSING_KEYWORD_PARAMS]: repairMissingKeywordParamsColumns,
    [RECOMMENDATION_TYPES.FIX_NO_IMPRESSION_BID]: fixNoImpressionBidColumns,
    [RECOMMENDATION_TYPES.IMPROVE_RESPONSIVE_SEARCH_ADS]: improveResponsiveSearchAdsColumns,
    [RECOMMENDATION_TYPES.REPAIR_UNTARGETED_OFFER]: repairUntargetedOfferColumns,
    [RECOMMENDATION_TYPES.IMPROVE_MULTIMEDIA_ADS]: improveMultiMediaAdsColumns,
    [RECOMMENDATION_TYPES.SSC_2_PMAX_MIGRATION]: SSC2PMaxMigrationColumns,
    [RECOMMENDATION_TYPES.PMAX_IMPORT]: PerformanceMaxImportColumns,
    [RECOMMENDATION_TYPES.DSA_2_PMAX_MIGRATION]: DSA2PMaxMigrationColumns,
    [RECOMMENDATION_TYPES.SYNDICATION_GAP]: SyndicationGapColumns,
  };
};

const getColumnsMapMCC = (permissions) => {
  const isMCC = true;
  // TODO: Removing estimate from KWO is not supported in MCC so far.
  // It will be supported after this feature is GAed.
  return {
    [RECOMMENDATION_TYPES.CALLOUT]: adExtensionColumnsMCC,
    [RECOMMENDATION_TYPES.SITE_LINK]: adExtensionColumnsMCC,
    [RECOMMENDATION_TYPES.REPAIR_ADS]: repairAdsColumnsMCC,
    [RECOMMENDATION_TYPES.REPAIR_KEYWORD]: repairKeywordsColumnsMCC,
    [RECOMMENDATION_TYPES.FIX_AD_TEXT]: fixAdTextColumnsMCC,
    [RECOMMENDATION_TYPES.BUDGET_OPPORTUNITY]: budgetColumns,
    [RECOMMENDATION_TYPES.ADJUST_SHARED_BUDGET_OPPORTUNITY]: adjustSharedBudgetColumns,
    [RECOMMENDATION_TYPES.GOOGLE_IMPORT]: googleImportColumns,
    [RECOMMENDATION_TYPES.GOOGLE_IMPORT_SCHEDULED]: googleImportColumns,
    [`${RECOMMENDATION_TYPES.BROAD_MATCH_KEYWORD}VNext`]: broadMatchKeywordColumnsVNextMCC,
    [`${RECOMMENDATION_TYPES.NEW_KEYWORD_OPPORTUNITY}VNext`]: newKeywordColumnsVNextMCC,
    [RECOMMENDATION_TYPES.TRENDING_QUERY]: trendingQueryColumnsMCC,
    [RECOMMENDATION_TYPES.REMOVE_CONFLICTING_NEGATIVE_KEYWORD]: removeConflictingNKWColumns,
    [RECOMMENDATION_TYPES.DYNAMIC_SEARCH_ADS]: dynamicSearchAdsColumnsMCC,
    [RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS]:
      getResponsiveSearchAdsColumns(permissions, isMCC),
    [RECOMMENDATION_TYPES.REALLOCATE_BUDGET]: ReallocateBudgetColumns,
    [RECOMMENDATION_TYPES.FIX_AD_DESTINATION]: fixAdsDestinationColumnsMCC,
    [RECOMMENDATION_TYPES.SETUP_CONVERSION_TRACKING_OPPORTUNITY]:
      SetupConversionTrackingOpportunityColumnsMCC,
    [RECOMMENDATION_TYPES.FIX_CONVERSION_TRACKING_OPPORTUNITY]: FixConversionTrackingColumns,
    [RECOMMENDATION_TYPES.FIX_CONVERSION_GOAL_SETTINGS]: fixConversionGoalSettingsColumns,
    [RECOMMENDATION_TYPES.CREATE_CONVERSION_GOAL]: createConversionGoalColumns,
    [RECOMMENDATION_TYPES.FACEBOOK_IMPORT]: facebookImportColumns,
    [RECOMMENDATION_TYPES.MULTIMEDIA_ADS]: multiMediaAdsColumnsMCC,
    [RECOMMENDATION_TYPES.IMAGE_EXTENSION]: imageExtensionColumnsMCC,
    [RECOMMENDATION_TYPES.REPAIR_MISSING_KEYWORD_PARAMS]: repairMissingKeywordParamsColumnsMCC,
    [RECOMMENDATION_TYPES.FIX_NO_IMPRESSION_BID]: fixNoImpressionBidColumnsMCC,
    [RECOMMENDATION_TYPES.IMPROVE_RESPONSIVE_SEARCH_ADS]: improveResponsiveSearchAdsColumnsMCC,
    [RECOMMENDATION_TYPES.REPAIR_UNTARGETED_OFFER]: repairUntargetedOfferColumnsMCC,
    [RECOMMENDATION_TYPES.IMPROVE_MULTIMEDIA_ADS]: improveMultiMediaAdsColumnsMCC,
    [RECOMMENDATION_TYPES.SSC_2_PMAX_MIGRATION]: SSC2PMaxMigrationColumnsMCC,
    [RECOMMENDATION_TYPES.PMAX_IMPORT]: PerformanceMaxImportColumnsMCC,
    [RECOMMENDATION_TYPES.DSA_2_PMAX_MIGRATION]: DSA2PMaxMigrationColumnsMCC,
    [RECOMMENDATION_TYPES.SYNDICATION_GAP]: SyndicationGapColumnsMCC,
  };
};

const getDefaultSortColumnMap = {
  [RECOMMENDATION_TYPES.REPAIR_ADS]:
    i18n => getSortableHeaderConfigByColumn(CampaignColumn, i18n, 0),
  [RECOMMENDATION_TYPES.REPAIR_KEYWORD]:
    i18n => getSortableHeaderConfigByColumn(CampaignColumn, i18n, 0),
  [RECOMMENDATION_TYPES.CALLOUT]:
    i18n => getSortableHeaderConfigByColumn(ClicksDeltaColumn, i18n),
  [RECOMMENDATION_TYPES.SITE_LINK]:
    i18n => getSortableHeaderConfigByColumn(ClicksDeltaColumn, i18n),
  [RECOMMENDATION_TYPES.BROAD_MATCH_KEYWORD]:
    i18n => getSortableHeaderConfigByColumn(ClicksDeltaColumn, i18n),
  [RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS]:
    i18n => getSortableHeaderConfigByColumn(CurrentCostColumn, i18n),
  [RECOMMENDATION_TYPES.BUDGET_OPPORTUNITY]: getSortableHeaderConfigByPrimaryEstimate,
  [RECOMMENDATION_TYPES.ADJUST_SHARED_BUDGET_OPPORTUNITY]: getSortableHeaderConfigByPrimaryEstimate,
  [RECOMMENDATION_TYPES.GOOGLE_IMPORT]: getSortableHeaderConfigNull,
  [RECOMMENDATION_TYPES.GOOGLE_IMPORT_SCHEDULED]: getSortableHeaderConfigNull,
  [RECOMMENDATION_TYPES.NEW_KEYWORD_OPPORTUNITY]: getSortableHeaderConfigNull,
  [RECOMMENDATION_TYPES.TRENDING_QUERY]: getSortableHeaderConfigNull,
  [RECOMMENDATION_TYPES.FIX_AD_TEXT]: getSortableHeaderConfigNull,
  [RECOMMENDATION_TYPES.DYNAMIC_SEARCH_ADS]: getSortableHeaderConfigNull,
  [RECOMMENDATION_TYPES.REALLOCATE_BUDGET]:
    i18n => getSortableHeaderConfigByColumn(ClicksDeltaColumn, i18n),
  [RECOMMENDATION_TYPES.SETUP_CONVERSION_TRACKING_OPPORTUNITY]:
    i18n => getSortableHeaderConfigByColumn(NumberClicksColumn, i18n),
  [RECOMMENDATION_TYPES.FIX_CONVERSION_GOAL_SETTINGS]:
    i18n => getSortableHeaderConfigByColumn(MissedConversionColumn, i18n, 0),
  [RECOMMENDATION_TYPES.FACEBOOK_IMPORT]: getSortableHeaderConfigNull,
  [RECOMMENDATION_TYPES.REPAIR_UNTARGETED_OFFER]:
    i18n => getSortableHeaderConfigByColumn(ImpressionsDeltaColumn, i18n),
};

export function getColumnsByType({
  recommendationType, i18n, newI18n, s$i18n, deps,
  view, readOnly = false, isMCC = false,
  scope, filterState, isOptimizationScoreOn = false,
}) {
  const { permissions } = deps;
  const map = isMCC ?
    getColumnsMapMCC(permissions) :
    getColumnsMap(permissions);
  const columns = (map[`${recommendationType}VNext`] && map[`${recommendationType}VNext`].slice(0))
    || map[recommendationType].slice(0);

  if (!_.contains(NO_ACTION_RECOMMENDATION_TYPES, recommendationType)) {
    columns.push(actionTypeTemplateMapping[INLINE_ACTION_MAP[recommendationType]]);
  }

  return mapColumnsConfig(columns, {
    i18n,
    newI18n,
    s$i18n,
    deps,
    recommendationType,
    view,
    readOnly,
    isMCC,
    scope,
    filterState,
    isOptimizationScoreOn,
  });
}

export function getDefaultSortColumn(recommendationType, i18n, primaryEstimate) {
  const f = getDefaultSortColumnMap[recommendationType] || getSortableHeaderConfigNull;
  return f(i18n, primaryEstimate);
}
